import { useState, type FormEvent } from 'react';
import { useNavigate, useLocation, Navigate } from 'react-router-dom';
import { useAuth } from '@/auth/AuthContext';
import { Button } from '@/components/ui/primitives';
import { ApiError, USE_MOCK_AUTH } from '@/lib/api';

/* /login — greenfield auth surface (brief v1 §3). Email + password → cookie
 * session via POST /v1/auth/login (REAL auth). The demo hint/autofill only
 * appears when VITE_USE_MOCK_AUTH=true, which is never the production build. */

/** Map the API's uniform auth error codes to human guidance. */
function loginErrorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    switch (err.code) {
      case 'invalid_credentials':
        return 'Invalid email or password.';
      case 'too_many_attempts':
      case 'rate_limited':
        return 'Too many attempts. Please wait a moment and try again.';
      case 'invalid_request':
        return 'Enter both your email and password.';
      case 'network_error':
        return 'Could not reach the server. Check your connection and try again.';
      default:
        return err.message || 'Sign-in failed. Please try again.';
    }
  }
  return 'Sign-in failed. Check your credentials.';
}
export function LoginPage() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const from = (location.state as { from?: string } | null)?.from ?? '/';

  if (user) return <Navigate to={from} replace />;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await login(email, password);
      navigate(from, { replace: true });
    } catch (err) {
      setError(loginErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-full items-center justify-center bg-canvas px-4">
      <div className="w-full max-w-sm">
        <div className="mb-8 text-center">
          <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-md bg-neutral-10 font-display text-lg font-bold text-neutral-0 dark:bg-neutral-11 dark:text-neutral-0">
            BC
          </div>
          <h1 className="font-display text-2xl font-semibold tracking-tight text-text-primary">
            BookedCentral
          </h1>
          <p className="mt-1 text-sm text-text-secondary">Run growth like an operating system.</p>
        </div>

        <form onSubmit={onSubmit} className="space-y-3 rounded-xl border border-border-subtle bg-surface p-6 shadow-sm">
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-text-secondary">Email</span>
            <input
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="h-control w-full rounded-md border border-border-default bg-canvas px-3 text-sm outline-none focus:border-ai focus:ring-2 focus:ring-ai/30"
              placeholder="you@company.com"
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-text-secondary">Password</span>
            <input
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="h-control w-full rounded-md border border-border-default bg-canvas px-3 text-sm outline-none focus:border-ai focus:ring-2 focus:ring-ai/30"
              placeholder="••••••••"
            />
          </label>

          {error && <p role="alert" className="text-sm text-danger">{error}</p>}

          <Button type="submit" variant="primary" disabled={busy} className="w-full">
            {busy ? 'Signing in…' : 'Sign in'}
          </Button>

          {USE_MOCK_AUTH && (
            <p className="pt-1 text-center text-[11px] text-text-tertiary">
              Demo mode — any credentials sign you in.
            </p>
          )}
        </form>

        <p className="mt-4 text-center text-[11px] text-text-tertiary">
          Powered by Concept Cubes
        </p>
      </div>
    </div>
  );
}
